import { useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import gcCrownIcon from '../../../assets/missions/gcCrown.png';
import giftIcon from '../../../assets/missions/gift.png';
import staticLockIcon from '../../../assets/missions/lock.png';
import lockIconJson from '../../../assets/missions/onboarding-lock-sprite.json';
import lockIconSprite from '../../../assets/missions/onboarding-lock-sprite.png';
import scCrownIcon from '../../../assets/missions/scCrown.png';
import { MOBILE_MEDIA_QUERY } from '../../../config/constants';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useSpriteSheet, type SpriteSheetAsset } from '../../../hooks/useSpriteSheet';
import { analytics } from '../../../services/analytics';
import { useStores } from '../../../stores/useStores';
import { SpriteAnimation } from '../../EventTriggerDialog/road/components/SpriteAnimation/SpriteAnimation';
import { DynamicTooltip } from '../../Tooltip/DynamicTooltip';
import { MISSIONS_TEST_ID_PREFIX } from '../constants';

import bar from './MissionsLobbyProgressBar.module.scss';
import classes from './MissionsOnboardingBar.module.scss';

const LOCK_SPRITE: SpriteSheetAsset = {
  imageSrc: lockIconSprite,
  dataSrc: JSON.stringify(lockIconJson),
};

// 23 frames over 0.6s
const LOCK_FRAME_INTERVAL_MS = 600 / 23;

// Exactly 1/3, not 0.33 — an odd canvas size half-pixel-centers in the wrapper and blurs the sprite.
// Mobile keeps the same canvas:wrapper ratio (88/44 vs 108/54); 88 stays an even integer.
const LOCK_SCALE = 1 / 3;
const LOCK_SCALE_MOBILE = 88 / 324;

const FILL_MS = 450;

// If the sprite never loads/completes (network failure), don't strand the unlock — skip to the gift.
const UNLOCK_WATCHDOG_MS = 4000;

type Phase = 'locked' | 'fill' | 'unlocking' | 'shrink' | 'gift';

export const MissionsOnboardingBar = observer(() => {
  const { t } = useTranslation();
  const { missionsStore, coinTypeStore, featureManagerStore } = useStores();
  const { coinType } = coinTypeStore;
  const { onboardingActiveMission: mission, isOnboardingTransition } = missionsStore;
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);

  const lockSprite = useSpriteSheet(LOCK_SPRITE);

  const [phase, setPhase] = useState<Phase>(() => (isOnboardingTransition ? 'fill' : 'locked'));
  const [fillPct, setFillPct] = useState(0);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [shaking, setShaking] = useState(false);

  const goal = mission?.goal ?? 0;
  const progress = mission?.progress ?? 0;
  const activePercentage = goal > 0 ? Math.min((progress / goal) * 100, 100) : 0;

  // rAF kick so the CSS width transition runs even when mounting straight into the transition state
  useEffect(() => {
    if (phase === 'locked') {
      setFillPct(activePercentage);
      return;
    }
    const id = requestAnimationFrame(() => setFillPct(100));
    return () => cancelAnimationFrame(id);
  }, [phase, activePercentage]);

  // TODO(MZ-488): swap for the admin-localized text once the key/namespace is confirmed
  const barText = t('missions.onboarding.barText', { count: goal });

  useEffect(() => {
    if (isOnboardingTransition) {
      setPhase('fill');
      setTooltipOpen(false);
    }
  }, [isOnboardingTransition]);

  useEffect(() => {
    if (phase !== 'fill') return;
    const id = setTimeout(() => setPhase('unlocking'), FILL_MS);
    return () => clearTimeout(id);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'unlocking') return;
    const id = setTimeout(() => setPhase('gift'), UNLOCK_WATCHDOG_MS);
    return () => clearTimeout(id);
  }, [phase]);

  const handleLockOpened = useCallback(() => setPhase('shrink'), []);

  const finalize = () => {
    // empty sc/gc in the transition read → animation only, no modal (ticket edge)
    const hasCurrentMissions = (missionsStore.currentMissions?.missions.length ?? 0) > 0;
    missionsStore.consumeOnboardingTransition();
    // mark the dialog as shown so the feature-manager sequence doesn't re-open it after close
    missionsStore.activeMissionShowed = true;
    if (hasCurrentMissions) {
      missionsStore.openDialog();
    } else {
      // release the slot in case startDialogs claimed it during the animation (no-op otherwise)
      featureManagerStore.onDialogFinished('Missions');
    }
  };

  const handleClick = () => {
    if (phase !== 'locked') return;
    void analytics.sendEvent('daily_missions', {
      event_category: 'daily_missions',
      event_name: 'daily_missions_onboarding',
      event_action: 'click',
      page_url: window.location.pathname,
    });
    setShaking(true);
    setTooltipOpen(true);
  };

  if (!mission) return null;

  return (
    <div className={bar.container} data-testid={`${MISSIONS_TEST_ID_PREFIX}-onboarding-bar`}>
      <div className={classNames(bar.crownIconWrapper, classes.crownShade, { [classes.locked]: phase === 'locked' })}>
        <img
          alt="crown"
          className={bar.crownIcon}
          data-testid={`${MISSIONS_TEST_ID_PREFIX}-onboarding-bar-crown-icon`}
          src={coinType === 'GC' ? gcCrownIcon : scCrownIcon}
        />
      </div>

      {/* text sits outside .bar so the locked dim filter never darkens it */}
      <div
        className={classNames(bar.barWrapper, classes.barWrapper)}
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        <div className={classNames(bar.bar, classes.barShade, { [classes.locked]: phase === 'locked' })}>
          <div className={classNames(bar.fill, classes.fill)} style={{ width: `${fillPct}%` }} />
        </div>
        <span
          className={classNames(bar.progressText, classes.progressText)}
          data-testid={`${MISSIONS_TEST_ID_PREFIX}-onboarding-bar-text`}
        >
          {barText}
        </span>
      </div>

      <div aria-hidden className={classes.lockWrapper} onClick={handleClick}>
        {phase === 'gift' ? (
          <img
            alt="gift"
            className={classNames(classes.gift, classes.giftIn)}
            data-testid={`${MISSIONS_TEST_ID_PREFIX}-onboarding-gift`}
            src={giftIcon}
            onAnimationEnd={finalize}
          />
        ) : (
          <div
            className={classNames(classes.lock, {
              [classes.lockShake]: shaking,
              [classes.lockShrink]: phase === 'shrink',
            })}
            onAnimationEnd={() => (phase === 'shrink' ? setPhase('gift') : setShaking(false))}
          >
            {/* until the sheet loads, SpriteAnimation's canvas is an unsized 300x150 invisible
                hit area — show the static lock instead */}
            {lockSprite.ready ? (
              <SpriteAnimation
                dataTestId={`${MISSIONS_TEST_ID_PREFIX}-onboarding-lock`}
                frameInterval={LOCK_FRAME_INTERVAL_MS}
                idleFrame={phase === 'shrink' ? 'last' : 'first'}
                isPlaying={phase === 'unlocking'}
                scale={isMobile ? LOCK_SCALE_MOBILE : LOCK_SCALE}
                sprite={lockSprite}
                onCycleComplete={handleLockOpened}
              />
            ) : (
              <img
                alt="locked"
                className={classes.lockPlaceholder}
                data-testid={`${MISSIONS_TEST_ID_PREFIX}-onboarding-lock`}
                src={staticLockIcon}
              />
            )}
          </div>
        )}
      </div>

      <DynamicTooltip
        arrowAlign="center"
        isOpen={tooltipOpen}
        position="bottom"
        tooltipClassName={classes.tooltip}
        onClose={() => setTooltipOpen(false)}
      >
        <span className={classes.tooltipText} data-testid={`${MISSIONS_TEST_ID_PREFIX}-onboarding-tooltip`}>
          {t('missions.onboarding.tooltip')}
        </span>
      </DynamicTooltip>
    </div>
  );
});
